// Eagerly fetches every parent-pay location's Recurly subscription data as
// soon as the account-header "Create Purchase Link" flow's combined Order
// Summary mounts (see ParentPayOrderSummary) — unlike the single-location
// flow, which lazily fetches only the location currently shown in the
// catalog panel (usePurchaseLinkLocation), every row here needs a real
// total immediately since they're all displayed at once. Confirmed
// trade-off: more upfront API calls, in exchange for accurate totals from
// first render instead of $0 rows until each location is visited.
import {useEffect, useRef} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {useAuthClient} from '../../../../../utils/use-auth-client'
import {
  selectLocations,
  setRecurlyAccountInfoForLocation,
} from '../../../../../app-store/PurchaseLinkSlice'
import {hasNoActiveSubscriptions} from './hasNoActiveSubscriptions'

// See usePurchaseLinkLocation's identical comment — a brand-new account's
// baseline "Command Center" subscriptions can still be provisioning
// server-side for a moment after account creation, so an empty result gets
// retried a few times before being accepted (and unlike a hard error, an
// empty result must NOT clear requestedRef, or every other effect re-run
// would refire it immediately instead of waiting out the retry delay).
const EMPTY_RESULT_RETRY_DELAY_MS = 1500
const MAX_EMPTY_RESULT_RETRIES = 3

export function usePurchaseLinkPreloadAllLocations() {
  const dispatch = useDispatch()
  const client = useAuthClient()
  const locations = useSelector(selectLocations)
  // Tracks which locations already have a fetch in flight/completed so the
  // effect re-running (locations is a new array reference on every location
  // update) never re-fires a request for the same location.
  const requestedRef = useRef(new Set())
  const retryCountsRef = useRef({})
  // Every location's fetch is issued in the same effect run, but each one
  // resolves at a different time and its dispatch gives `locations` a new
  // array reference, which reruns this effect. A single per-run `cancelled`
  // flag closed over by every in-flight promise from that run used to get
  // set on the *next* run's cleanup as soon as the first location resolved,
  // silently dropping every other location's still-in-flight response (and
  // requestedRef then blocked them from ever being retried) — so only
  // whichever location happened to resolve first ever got real data. Guard
  // against real unmount only, via a ref that survives effect reruns,
  // instead of a flag scoped to one run.
  const isMountedRef = useRef(true)
  useEffect(() => {
    return () => {
      isMountedRef.current = false
    }
  }, [])

  useEffect(() => {
    const fetchLocation = loc => {
      client(`accounts/5/subscriptions?thryvId=${loc.thryvId}`)
        .then(recurlyAccountInfo => {
          if (!isMountedRef.current) return
          const retryCount = retryCountsRef.current[loc.locationId] ?? 0
          if (
            hasNoActiveSubscriptions(recurlyAccountInfo) &&
            retryCount < MAX_EMPTY_RESULT_RETRIES
          ) {
            retryCountsRef.current[loc.locationId] = retryCount + 1
            setTimeout(() => {
              if (isMountedRef.current) fetchLocation(loc)
            }, EMPTY_RESULT_RETRY_DELAY_MS)
            return
          }
          dispatch(
            setRecurlyAccountInfoForLocation({
              locationId: loc.locationId,
              recurlyAccountInfo,
            }),
          )
        })
        .catch(() => {
          // Allow a retry on the next effect run (e.g. after switching
          // locations triggers a re-render) instead of leaving this
          // location permanently unfetched.
          requestedRef.current.delete(loc.locationId)
        })
    }

    locations.forEach(loc => {
      if (loc.isLoaded) return
      if (!loc.thryvId) return
      if (requestedRef.current.has(loc.locationId)) return
      requestedRef.current.add(loc.locationId)
      fetchLocation(loc)
    })
  }, [locations, client, dispatch])
}
